import { useCallback, useRef } from 'react';
import type { TabBlockHandle } from '../../core/types';
import { BlockEventType } from '../../core/constants';
import { useEventScope } from '../useEventScope';
import { useBlockHandle } from '../useBlockHandle';

interface UseTabEventAdapterOptions {
  setBadge: (tabId: string, value: string | number | null) => void;
  setActiveTab: (tabId: string) => void;
  getActiveTab: () => string;
}

export function useTabEventAdapter(
  semanticId: string | undefined,
  options: UseTabEventAdapterOptions,
) {
  const scope = useEventScope();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const publishTabChanged = useCallback(
    (tabId: string, previousTabId: string) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.TAB_CHANGED, semanticId, {
        tabId,
        previousTabId,
      });
    },
    [scope, semanticId],
  );

  const publishRef = useRef(publishTabChanged);
  publishRef.current = publishTabChanged;

  const handleFactory = useCallback((): TabBlockHandle => {
    return {
      semanticId: semanticId!,
      blockType: 'tab',
      capabilities: new Set(['setBadge', 'setActiveTab']),
      setBadge: (tabId, value) => optionsRef.current.setBadge(tabId, value),
      setActiveTab: (tabId) => {
        const prev = optionsRef.current.getActiveTab();
        optionsRef.current.setActiveTab(tabId);
        if (tabId !== prev) {
          publishRef.current(tabId, prev);
        }
      },
      dispose: () => {},
    };
  }, [semanticId]);

  useBlockHandle(semanticId, handleFactory, [semanticId]);

  return {
    publishTabChanged,
    scope,
  };
}
